/**
 * LEGACY: Explore page (V1)
 * This page uses the legacy Copilot app credentials for Data Copilot.
 * New workflow V2 uses ExploreStep.tsx which uses TotalSERP app + profile_id instead.
 * 
 * Route: /explore/client/:clientId/:workflowId/view
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  Box,
  Typography,
  Button as MuiButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { 
  OpenInFull, 
  CloseFullscreen, 
  Home as HomeIcon, 
  ArrowBack as ArrowBackIcon,
  Explore as ExploreIcon,
  FaceRetouchingNatural as CopilotIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight
} from '@mui/icons-material';
import WorkflowStepper from '@/components/WorkflowStepper';
import { getWorkflowStatus } from '@/utils/moduleStatus';
import { useWorkflow } from '../hooks/useWorkflow';
import { useDatabaseMode } from '../context/DatabaseModeContext';
import { clientService } from '../services/api/clientService';
import { snowflakeService } from '../services/snowflakeService';
import DatabaseStatusIndicator from '../components/common/DatabaseStatusIndicator';
import DatabaseMigrationBanner from '../components/common/DatabaseMigrationBanner';
import DashboardNavigation from '../components/DashboardNavigation';
import AIAnalyst from '../components/AIAnalyst';

// Question interface (shared with AIAnalyst)
interface Question {
  id?: string;
  title?: string;
  description?: string;
  category: string;
  question?: string;
  icon: React.ReactNode | string;
  prompt?: string;
  color?: string;
  conversation_id?: string;
  status?: 'pending' | 'completed' | 'failed';
  message_id?: string;
  profile_id?: string;
  error?: string;
}

const Explore: React.FC = () => {
  const navigate = useNavigate();
  const { workflowId: workflowIdStr, clientId: clientIdStr } = useParams<{
    workflowId: string;
    clientId: string;
  }>();

  // Convert URL parameters
  const workflowId = workflowIdStr;
  const clientId = clientIdStr;

  // Parse numeric IDs for database operations
  const numericClientId =
    clientIdStr && /^\d+$/.test(clientIdStr) ? parseInt(clientIdStr, 10) : undefined;

  const { isUsingDatabaseApproach } = useDatabaseMode();
  const [isStepperCollapsed, setIsStepperCollapsed] = useState(false);
  const [isExplorerMaximized, setIsExplorerMaximized] = useState(false);
  const [clientData, setClientData] = useState<any>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Dashboard state - simplified for new navigation approach
  const [dashboardError, setDashboardError] = useState<string>('');
  const [selectedDashboard, setSelectedDashboard] = useState<any>(null);

  // AI Analyst questions state
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState<string>('');

  // Handle dashboard change - close Copilot when switching dashboards
  const handleSelectedDashboardChange = (dashboard: any) => {
    // If switching to a different dashboard, close Copilot panel
    if (selectedDashboard && dashboard && selectedDashboard.Id !== dashboard.Id) {
      setIsCopilotOpen(false);
      setCopilotUrl(null);
      setIsDashboardHidden(false);
      setDashboardWidth(60); // Reset to default split
    }
    setSelectedDashboard(dashboard);
  };

  // Copilot state
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [copilotUrl, setCopilotUrl] = useState<string | null>(null);
  const [isLoadingCopilot, setIsLoadingCopilot] = useState(false);
  const [copilotError, setCopilotError] = useState<string | null>(null);
  const [dashboardWidth, setDashboardWidth] = useState(60); // Default 60% for dashboard
  const [isDashboardHidden, setIsDashboardHidden] = useState(false);
  const [createdConversations, setCreatedConversations] = useState<Set<string>>(new Set());

  const {
    workflow,
    isLoading: isWorkflowLoading,
    error: workflowError,
  } = useWorkflow(workflowId, isUsingDatabaseApproach);

  // Handler for stepper collapse change
  const handleStepperCollapseChange = (collapsed: boolean) => {
    setIsStepperCollapsed(collapsed);
  };

  // Handler for explorer maximize/minimize toggle
  const handleExplorerToggle = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsExplorerMaximized(!isExplorerMaximized);
  };

  // Handler for tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (isExplorerMaximized) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActiveTab(newValue);
  };

  // Handler for Copilot toggle
  const handleCopilotToggle = async () => {
    if (!isCopilotOpen) {
      // Opening Copilot - load the chat interface
      await loadCopilot();
    } else {
      // Closing Copilot - reset dashboard visibility
      setIsDashboardHidden(false);
    }
    setIsCopilotOpen(!isCopilotOpen);
  };

  // Resizer handlers
  const [isResizing, setIsResizing] = useState(false);
  const [dragIndicatorX, setDragIndicatorX] = useState<number | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const container = document.querySelector('[data-dashboard-container]') as HTMLElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const relativeX = e.clientX - containerRect.left;
    const newWidthPercent = (relativeX / containerRect.width) * 100;
    
    // Constrain between 30% and 80% for normal mode, 20% and 85% for maximized
    const minWidth = isExplorerMaximized ? 20 : 30;
    const maxWidth = isExplorerMaximized ? 85 : 80;
    const constrainedWidth = Math.min(Math.max(newWidthPercent, minWidth), maxWidth);
    
    // Convert back to pixel position for the indicator
    const indicatorX = (constrainedWidth / 100) * containerRect.width;
    setDragIndicatorX(indicatorX);
  }, [isResizing, isExplorerMaximized]);

  const handleMouseUp = useCallback(() => {
    if (isResizing && dragIndicatorX !== null) {
      const container = document.querySelector('[data-dashboard-container]') as HTMLElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const newWidthPercent = (dragIndicatorX / containerRect.width) * 100;
        setDashboardWidth(newWidthPercent);
      }
    }
    setIsResizing(false);
    setDragIndicatorX(null);
  }, [isResizing, dragIndicatorX]);

  useEffect(() => {
    if (isResizing) {
      // Use capture phase to ensure we get events even when over child elements
      document.addEventListener('mousemove', handleMouseMove, true);
      document.addEventListener('mouseup', handleMouseUp, true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      // Prevent pointer events on all child elements during drag
      document.body.style.pointerEvents = 'none';
      // But allow pointer events on the document itself
      document.documentElement.style.pointerEvents = 'auto';
    } else {
      document.removeEventListener('mousemove', handleMouseMove, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      document.documentElement.style.pointerEvents = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      document.documentElement.style.pointerEvents = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Load questions from Guardrail and sync with database status
  const loadQuestionsFromGuardrail = async () => {
    try {
      setQuestionsLoading(true);
      setQuestionsError('');
      
      const response = await snowflakeService.getGuardrailInstruction('TotalSERP_Analytics_Questions');
      
      if (response.success && response.data?.content) {
        const guardrailQuestions = response.data.content;
        
        // Ensure it's an array
        if (Array.isArray(guardrailQuestions)) {
          // Load existing conversation statuses from database
          let conversationsByQuestion: Record<string, { conversation_id: string; error_message: string | null }> = {};
          if (workflowId) {
            try {
              const conversationsResponse = await snowflakeService.getWorkflowConversations(workflowId);
              if (conversationsResponse.success && conversationsResponse.data) {
                conversationsByQuestion = conversationsResponse.data;
              }
            } catch (error) {
              console.error('Error loading conversation statuses:', error);
            }
          }
          
          // Initialize questions with database status
          const questionsWithStatus = guardrailQuestions.map(question => {
            const questionId = question.id;
            const conversationData = conversationsByQuestion[questionId];
            
            // Determine status based on database conversation
            let status: 'pending' | 'completed' | 'failed' = 'pending';
            let error: string | undefined;
            let conversationId: string | undefined;
            
            if (conversationData) {
              const { conversation_id, error_message } = conversationData;
              
              if (conversation_id === 'failed') {
                // Real exception/failure - no conversation ID
                status = 'failed';
                error = error_message || 'Error processing question';
              } else if (conversation_id && conversation_id !== 'pending') {
                // Completed (may have error from backend service like Error 399504)
                status = 'completed';
                conversationId = conversation_id;
                error = error_message || undefined;
              } else if (conversation_id === 'pending') {
                status = 'pending';
              }
            }
            
            return {
              ...question,
              status,
              conversation_id: conversationId,
              error
            };
          });
          
          setQuestions(questionsWithStatus);
        } else {
          setQuestions([]);
          setQuestionsError('Invalid question format received');
        }
      } else {
        setQuestions([]);
        setQuestionsError(response.error || 'Failed to load questions');
      }
    } catch (error) {
      console.error('Error loading questions from Guardrail:', error);
      setQuestions([]);
      setQuestionsError('Failed to connect to Guardrail service');
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Send questions to Guardrail chat API and update conversation_id from response
  const sendQuestionsToGuardrailChat = async () => {
    const metadata: any = {
      client_id: clientId,
      client_name: clientData?.name,
      client_domains: clientData?.domains,
      workflow_id: workflowId
    };

    // Process questions in parallel
    const chatPromises = questions.map(async (question, index) => {
      if (question.question && question.status === 'pending') {
        try {
          metadata.question_id = question.id;
          
          const chatResponse = await snowflakeService.sendGuardrailChatMessage({
            message: question.question,
            metadata
          });

          if (chatResponse.success && chatResponse.data?.conversation_id) {
            // Handle both existing and new conversation responses
            const conversationId = chatResponse.data.conversation_id;
            
            // Update the question with the conversation_id from the response
            setQuestions(prevQuestions => 
              prevQuestions.map((q, qIndex) => 
                qIndex === index 
                  ? { 
                      ...q, 
                      conversation_id: conversationId,
                      status: conversationId === 'pending' ? 'pending' : 'completed'
                    }
                  : q
              )
            );
          } else {
            // Mark as failed but keep the question
            setQuestions(prevQuestions => 
              prevQuestions.map((q, qIndex) => 
                qIndex === index 
                  ? { ...q, status: 'failed', error: chatResponse.error || 'Failed to process question' }
                  : q
              )
            );
          }
        } catch (error: any) {
          // Mark as failed
          setQuestions(prevQuestions => 
            prevQuestions.map((q, qIndex) => 
              qIndex === index 
                ? { ...q, status: 'failed', error: error.message || 'Network error occurred' }
                : q
            )
          );
        }
      }
    });

    // Wait for all questions to be processed
    try {
      await Promise.allSettled(chatPromises);
    } catch (error) {
      // Silently handle errors - individual question errors are already handled above
    }
  };

  const loadCopilot = async () => {
    try {
      setIsLoadingCopilot(true);
      setCopilotError(null);

      const sessionData = await snowflakeService.createGuardrailCopilotSession();

      // Get conversation_id from current dashboard
      const conversationId = selectedDashboard?.conversation_id;
      
      if (!conversationId) {
        throw new Error('No conversation ID available from selected dashboard');
      }

      // Check if we've already created this conversation
      if (!createdConversations.has(conversationId)) {
        try {
          await snowflakeService.createGuardrailConversation(conversationId);
          console.log('Guardrail conversation created successfully');
          
          // Mark this conversation as created
          setCreatedConversations(prev => new Set(prev).add(conversationId));
        } catch (conversationError) {
          console.warn('Failed to create guardrail conversation:', conversationError);
          // Continue anyway - the conversation might already exist
          // Still mark it as "created" to avoid repeated attempts
          setCreatedConversations(prev => new Set(prev).add(conversationId));
        }
      } else {
        console.log('Guardrail conversation already tracked as created');
      }
      
      // Build the base URL using the conversation_id path
      let url = `${sessionData.base_url}/embed/chat/${conversationId}?theme=light&token=${sessionData.token}`;

      // Add client_id and client_domains as query parameters if available
      if (clientId) {
        url += `&client_id=${encodeURIComponent(clientId)}`;
      }

      if (clientData?.domains) {
        url += `&client_domains=${encodeURIComponent(clientData.domains)}`;
      }
      
      if (clientData?.name) {
        url += `&client_name=${encodeURIComponent(clientData.name)}`;
      }

      setCopilotUrl(url);
    } catch (err) {
      console.error('Failed to create Copilot session:', err);
      setCopilotError('Failed to load Copilot. Please try again.');
    } finally {
      setIsLoadingCopilot(false);
    }
  };

  const iframeStyle = {
    border: 'none',
    display: 'flex',
    flex: 1,
    height: '100%',
    minHeight: isExplorerMaximized ? 0 : '500px',
    margin: 0,
    padding: 0
  };

  const tabContentStyle = {
    flex: 1,
    flexDirection: 'column',
    border: isExplorerMaximized ? 'none' : '1px solid #e0e0e0',
    borderTop: 'none',
    borderRadius: isExplorerMaximized ? 0 : 1,
    backgroundColor: 'transparent',
    boxShadow: isExplorerMaximized ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
    mx: 0, // Remove horizontal margins
    p: isExplorerMaximized ? '0px' : '2px'  // Remove horizontal padding
  }

    
  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      if (numericClientId) {
        try {
          const client = await clientService.getClient(numericClientId);
          setClientData(client);
        } catch (error) {
          console.error('Failed to fetch client data:', error);
        }
      }
    };

    fetchClientData();
  }, [numericClientId]);

  // Load questions when component mounts
  useEffect(() => {
    loadQuestionsFromGuardrail();
  }, []);

  // Handler for refreshing a specific question
  const handleRefreshQuestion = async (questionId: string) => {
    // Find the question
    const question = questions.find(q => q.id === questionId);
    if (!question || !question.question) return;

    // Mark it as pending - polling will update it when complete
    setQuestions(prevQuestions =>
      prevQuestions.map(q =>
        q.id === questionId
          ? { ...q, status: 'pending' as const, conversation_id: undefined }
          : q
      )
    );

    // Send the refresh request with force_run flag (fire and forget)
    const metadata: any = {
      client_id: clientId,
      client_name: clientData?.name,
      client_domains: clientData?.domains,
      workflow_id: workflowId,
      question_id: questionId,
      force_run: true
    };

    snowflakeService.sendGuardrailChatMessage({
      message: question.question,
      metadata
    }).catch(error => {
      console.error('Error sending refresh request:', error);
      // Optionally mark as failed if the request itself fails
      setQuestions(prevQuestions =>
        prevQuestions.map(q =>
          q.id === questionId
            ? { ...q, status: 'failed', error: 'Failed to send refresh request' }
            : q
        )
      );
    });
  };

  // Handler for refreshing all questions
  const handleRefreshAllQuestions = async () => {
    // Get ALL questions regardless of status (completed, failed, or even pending)
    const allQuestions = questions.filter(q => q.question && q.id);
    
    if (allQuestions.length === 0) return;

    // Mark all questions as pending
    setQuestions(prevQuestions =>
      prevQuestions.map(q => ({
        ...q,
        status: 'pending' as const,
        conversation_id: undefined,
        error: undefined
      }))
    );

    // Send refresh requests for all questions with force_run flag (fire and forget)
    allQuestions.forEach(question => {
      const metadata: any = {
        client_id: clientId,
        client_name: clientData?.name,
        client_domains: clientData?.domains,
        workflow_id: workflowId,
        question_id: question.id,
        force_run: true
      };

      snowflakeService.sendGuardrailChatMessage({
        message: question.question!,
        metadata
      }).catch(error => {
        console.error(`Error sending refresh request for question ${question.id}:`, error);
        // Mark this specific question as failed
        setQuestions(prevQuestions =>
          prevQuestions.map(q =>
            q.id === question.id
              ? { ...q, status: 'failed', error: 'Failed to send refresh request' }
              : q
          )
        );
      });
    });
  };

  // Polling function to check for completed conversations
  const pollForCompletedConversations = useCallback(async () => {
    if (!workflowId) return;

    try {
      // Get conversations for this workflow
      const response = await snowflakeService.getWorkflowConversations(workflowId);
      
      if (response.success && response.data) {
        const conversationsByQuestion = response.data;
        
        // Update questions with completed or failed conversations
        setQuestions(prevQuestions => 
          prevQuestions.map(question => {
            if (question.id && conversationsByQuestion[question.id]) {
              const conversationData = conversationsByQuestion[question.id];
              const { conversation_id, error_message } = conversationData;
              
              // Check for failed status (real exception)
              if (conversation_id === 'failed') {
                return {
                  ...question,
                  conversation_id: undefined,
                  status: 'failed',
                  error: error_message || 'Error processing question'
                };
              }
              
              // Check for valid (non-pending) conversation ID
              if (conversation_id && conversation_id !== 'pending') {
                return {
                  ...question,
                  conversation_id: conversation_id,
                  status: 'completed',
                  error: error_message || undefined  // Preserve error from backend service
                };
              }
              
              // If it's 'pending' in the database, make sure frontend reflects that
              if (conversation_id === 'pending' && question.status !== 'pending') {
                return {
                  ...question,
                  status: 'pending',
                  conversation_id: undefined,
                  error: undefined
                };
              }
            }
            return question;
          })
        );
      }
    } catch (error: any) {
      console.error('Error polling for completed conversations:', error);
    }
  }, [workflowId]);

  // Set up polling for pending conversations
  useEffect(() => {
    const hasPendingQuestions = questions.some(q => q.status === 'pending');
    
    if (hasPendingQuestions) {
      console.log('Starting polling - pending questions detected');
      const pollInterval = setInterval(pollForCompletedConversations, 3000); // Poll every 3 seconds
      
      return () => {
        console.log('Stopping polling - no pending questions');
        clearInterval(pollInterval);
      };
    } else {
      console.log('No pending questions - polling not started');
    }
  }, [questions, pollForCompletedConversations]);

  // Process questions through Guardrail chat API when questions are loaded
  useEffect(() => {
    if (questions.length > 0 && clientData) {
      sendQuestionsToGuardrailChat();
    }
  }, [questions.length, clientData]);

  // Adjust dashboard width when switching between maximized and normal modes
  useEffect(() => {
    if (isCopilotOpen) {
      // Adjust width constraints based on mode
      const minWidth = isExplorerMaximized ? 20 : 30;
      const maxWidth = isExplorerMaximized ? 85 : 80;
      
      if (dashboardWidth < minWidth) {
        setDashboardWidth(minWidth);
      } else if (dashboardWidth > maxWidth) {
        setDashboardWidth(maxWidth);
      }
    }
  }, [isExplorerMaximized, isCopilotOpen, dashboardWidth]);

  if (workflowError) {
    return (
      <div className="container mx-auto py-8">
        <Typography color="error" variant="h6">
          Error loading workflow: {workflowError.message}
        </Typography>
      </div>
    );
  }

  return (
    <Box
      className={isExplorerMaximized ? '' : 'container mx-auto py-6'}
      sx={{
        display: 'flex',
        py: isExplorerMaximized ? 0 : undefined,
        height: isExplorerMaximized ? '100vh' : 'calc(100vh - 154px)',
        width: isExplorerMaximized ? '100vw' : '100%',
        gap: isExplorerMaximized ? 0 : 4,
        position: isExplorerMaximized ? 'fixed' : 'relative',
        top: isExplorerMaximized ? 0 : 'auto',
        left: isExplorerMaximized ? 0 : 'auto',
        zIndex: isExplorerMaximized ? 9999 : 'auto',
        backgroundColor: isExplorerMaximized ? 'background.default' : 'transparent',
      }}
    >
      {/* Left Side: WorkflowStepper - Fixed sidebar when not maximized */}
      {!isExplorerMaximized && (
        <WorkflowStepper
          activeStep={7} // Explore is step 7 (0-indexed)
          workflowId={workflowId || ''}
          clientId={clientId}
          clientName={clientData?.name}
          moduleCompletionStatus={getWorkflowStatus(workflowId)}
          onCollapseChange={handleStepperCollapseChange}
          sx= {{
            height: 'calc(100vh - 188px)',
          }}
        />
      )}

             {/* Right Side: Main content area */}
       <Box
         sx={{
           flex: 1,
           display: 'flex',
           flexDirection: 'column',
           minWidth: 0, // Prevents flex item from overflowing
           height: '100%',
           overflowY: 'auto',
           px: 0 // Remove all horizontal padding
         }}
       >
        {/* Breadcrumb navigation with back button - Hide when maximized */}
        {!isExplorerMaximized && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Breadcrumbs 
              aria-label="breadcrumb" 
              sx={{ '& .MuiBreadcrumbs-separator': { color: 'text.secondary' } }}
            >
              <Link 
                color="inherit" 
                href="/" 
                onClick={(e) => { e.preventDefault(); navigate('/'); }}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                Dashboard
              </Link>
              <Typography color="text.secondary">
                {clientData?.name || `Client ${clientId}`} ({clientId} - {workflowId})
              </Typography>
              <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                <ExploreIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Results Explorer
              </Typography>
            </Breadcrumbs>
            
            <MuiButton
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/start-process')}
              sx={{ ml: 2 }}
            >
              Start Process
            </MuiButton>
          </Box>
        )}

        {/* Database Migration Banner - Hide when maximized */}
        {!isExplorerMaximized && <DatabaseMigrationBanner />}

        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: isExplorerMaximized ? 1 : 'auto'
          }}
        >

                                 {/* Tabs */}
                 <Box
                   sx={{
                     borderBottom: 1,
                     borderColor: 'divider',
                     mb: 0,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'space-between',
                     position: 'relative',
                     zIndex: isExplorerMaximized ? 10 : 'auto',
                     mx: 0, // Remove horizontal margins
                     px: isExplorerMaximized ? 1 : 0, // Minimal padding for maximized mode controls
                   }}
                  onClick={
                    isExplorerMaximized
                      ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      : undefined
                  }
                >
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="explorer tabs"
                    onClick={
                      isExplorerMaximized
                        ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        : undefined
                    }
                  >
                    <Tab
                      label="Dashboard"
                      onClick={
                        isExplorerMaximized
                          ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          : undefined
                      }
                    />
                    <Tab
                      label="AI Analyst"
                      onClick={
                        isExplorerMaximized
                          ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          : undefined
                      }
                    />
                  </Tabs>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* Copilot button - only show on Dashboard tab */}
                    {activeTab === 0 && (
                      <Tooltip
                        title={isCopilotOpen ? 'Close Data Copilot' : 'Open Data Copilot'}
                        placement="left"
                        arrow
                      >
                        <MuiButton
                          onClick={handleCopilotToggle}
                          startIcon={<CopilotIcon />}
                          variant="text"
                          size="small"
                          sx={{ 
                            color: isCopilotOpen ? 'warning.dark' : 'grey.600',
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: 'auto',
                            px: 1,
                            py: 0.5,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                          aria-label={isCopilotOpen ? 'Close Data Copilot' : 'Open Data Copilot'}
                        >
                          Data Copilot
                        </MuiButton>
                      </Tooltip>
                    )}

                    {/* Maximize/minimize toggle - always positioned on right side of tabs */}
                    <Tooltip
                      title={isExplorerMaximized ? 'Exit full screen mode' : 'Enter full screen mode'}
                      placement="left"
                      arrow
                    >
                      <IconButton
                        onClick={handleExplorerToggle}
                        sx={{ p: 0.5 }}
                        aria-label={
                          isExplorerMaximized ? 'Exit full screen mode' : 'Enter full screen mode'
                        }
                      >
                        {isExplorerMaximized ? <CloseFullscreen /> : <OpenInFull />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Tab Content */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {/* Dashboard Tab */}
                  <Box
                     data-dashboard-container
                     sx={{
                      ...tabContentStyle,
                       display: activeTab === 0 ? 'flex' : 'none',
                       flexDirection: 'row', // Change to row for split layout
                     }}
                   >
                    {/* Dashboard Content */}
                    <Box
                      sx={{
                        width: isCopilotOpen ? (isDashboardHidden ? '0%' : `${dashboardWidth}%`) : '100%',
                        transition: 'width 0.3s ease-in-out',
                        display: isDashboardHidden ? 'none' : 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        borderRight: isCopilotOpen && !isDashboardHidden ? '1px solid #e0e0e0' : 'none',
                      }}
                    >
                      {dashboardError ? (
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: 1
                        }}>
                          <Alert severity="error" sx={{ mb: 3, maxWidth: '600px' }}>
                            <Typography variant="body2">{dashboardError}</Typography>
                          </Alert>
                        </Box>
                      ) : (
                        <DashboardNavigation
                          clientId={clientId}
                          workflowId={workflowId}
                          clientData={clientData}
                          iframeStyle={iframeStyle}
                          isMaximized={isExplorerMaximized}
                          onSelectedDashboardChange={handleSelectedDashboardChange}
                        />
                      )}
                    </Box>

                    {/* Resizer Divider */}
                    {isCopilotOpen && !isDashboardHidden && (
                      <Box
                        onMouseDown={handleMouseDown}
                        sx={{
                          width: '4px',
                          cursor: 'col-resize',
                          backgroundColor: 'divider',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                          },
                          transition: 'background-color 0.2s ease',
                          flexShrink: 0,
                        }}
                      />
                    )}

                    {/* Drag Overlay - captures all mouse events during drag */}
                    {isResizing && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 999,
                          cursor: 'col-resize',
                          backgroundColor: 'transparent',
                        }}
                      />
                    )}

                    {/* Drag Indicator Line */}
                    {isResizing && dragIndicatorX !== null && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${dragIndicatorX}px`,
                          top: 0,
                          bottom: 0,
                          width: '2px',
                          backgroundColor: 'primary.main',
                          zIndex: 1000,
                          pointerEvents: 'none',
                          boxShadow: '0 0 4px rgba(25, 118, 210, 0.5)',
                        }}
                      />
                    )}

                    {/* Copilot Panel */}
                    {isCopilotOpen && (
                      <Box
                        sx={{
                          width: isDashboardHidden ? '100%' : `${100 - dashboardWidth}%`,
                          transition: 'width 0.3s ease-in-out',
                          display: 'flex',
                          flexDirection: 'column',
                          minWidth: 0,
                          backgroundColor: 'background.paper',
                        }}
                      >
                        {/* Copilot Header */}
                        <Box
                          sx={{
                            p: isExplorerMaximized ? 0.5 : 1,
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: 'grey.50',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => setIsDashboardHidden(!isDashboardHidden)}
                              sx={{ p: 0.5 }}
                              title={isDashboardHidden ? 'Show Dashboard' : 'Hide Dashboard'}
                            >
                              <KeyboardArrowLeft fontSize="small" />
                            </IconButton>
                            <Typography 
                              variant={isExplorerMaximized ? "body2" : "subtitle2"} 
                              sx={{ fontWeight: 600 }}
                            >
                              Copilot
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setIsCopilotOpen(false);
                              setIsDashboardHidden(false); // Reset dashboard visibility when closing Copilot
                            }}
                            sx={{ p: 0.5 }}
                            title="Close Copilot"
                          >
                            <KeyboardArrowRight fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* Copilot Content */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          {!copilotUrl ? (
                            <Box sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flex: 1,
                              color: 'text.secondary',
                              p: 2
                            }}>
                              {isLoadingCopilot ? (
                                <>
                                  <CircularProgress size={20} sx={{ mr: 1 }} />
                                  Loading Copilot...
                                </>
                              ) : copilotError ? (
                                <>
                                  <Box sx={{ color: 'error.main', mb: 2 }}>
                                    {copilotError}
                                  </Box>
                                  <MuiButton variant="contained" onClick={loadCopilot}>
                                    Try again
                                  </MuiButton>
                                </>
                              ) : (
                                'Initializing Copilot...'
                              )}
                            </Box>
                          ) : (
                            <iframe
                              src={copilotUrl}
                              width="100%"
                              height="100%"
                              allow="clipboard-write"
                              style={{
                                border: 'none',
                                flex: 1,
                                minHeight: '400px',
                              }}
                              title="Copilot Chat"
                            />
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* AI Analyst Tab */}
                   <Box
                     sx={{
                       ...tabContentStyle,
                       display: activeTab === 1 ? 'flex' : 'none',
                     }}
                   >
                    <AIAnalyst
                      clientId={clientId}
                      clientData={clientData}
                      isMaximized={isExplorerMaximized}
                      isActive={activeTab === 1}
                      questions={questions}
                      questionsLoading={questionsLoading}
                      questionsError={questionsError}
                      workflowId={workflowId}
                      onRefreshQuestion={handleRefreshQuestion}
                      onRefreshAllQuestions={handleRefreshAllQuestions}
                    />
                  </Box>
                </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Explore;
